import React, { useEffect, useRef, useState } from "react";
import LaddaButton from "../../components/buttons/laddaButton";
import { Link, useLocation } from "react-router-dom";
import BreadCrumbComp from "../../components/breadCrumb";
import CompetenceGraphSection from "./graphSection/competenceGraph";
import ComparisonGraphSection from "./graphSection/comparisonGraph";
import Total360Graph from "./graphSection/total360Graph";
import TabComponent from "../../components/tabComponent";
import "./styles.css";
import { useSelector } from "react-redux";
import { AuthorizedComponent } from "../../components/autorizedComponent";
import { Page360report } from "../../utils/AuthorizedUtils/360Report";
import { Alert, CircularProgress, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import ObservationsTab from "./observations";
import {
  callApiAsync,
  callActionApiAsync,
  getCallManagerReport360GraphData,
  getCallManagerTotal360GraphData,
  getCallOrganizationalReport360GraphData,
  getCallReport360Observations,
  getCallSpecifyReport360GraphData,
  getSettingById,
} from "../../services/apiService";

const readReportData = (apiFunc) => new Promise((resolve, reject) => {
  callActionApiAsync(apiFunc, resolve, reject);
});

const Report_360 = () => {
  const location = useLocation();
  const { rowData } = location.state || {};
  const availableYears = [...new Set([
    ...(Array.isArray(rowData?.evaluationYears) ? rowData.evaluationYears : []),
    ...(rowData?.year ? [rowData.year] : [])
  ].map(Number))].filter((year) => Number.isInteger(year) && year >= 1900).sort((a, b) => b - a);
  const [selectedYear, setSelectedYear] = useState(() => rowData?.year ? Number(rowData.year) : availableYears[0] ?? "");
  const reportKey = `${rowData?.id}-${selectedYear}`;
  const currentReportKey = useRef(reportKey);
  currentReportKey.current = reportKey;
  const [loadedReportKey, setLoadedReportKey] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportReload, setReportReload] = useState(0);

  const [observationsData, setObservationsData] = useState(null);
  const [managerReport, setManagerReport] = useState([]);
  const [managerReportTotal, setManagerReportTotal] = useState([]);
  const [specifyReport, setSpecifyReport] = useState([]);
  const [organizationalReport, setOrganizationalReport] = useState([]);
  const [organizationalUmbral, setOrganizationalUmbral] = useState(0);
  const [specifyUmbral, setSpecifyUmbral] = useState(0);

  const userData = useSelector((state) => state.userData.userData);

  const getObservationsData = async () => {
    const userId = rowData?.id;
    if (userId !== undefined) {
      await callActionApiAsync(
        () => getCallReport360Observations(userId, selectedYear),
        (data) => {
          if (currentReportKey.current !== reportKey) return;
          if (Number(data.evaluationTimeframeId) !== Number(observationsData?.evaluationTimeframeId)) {
            setLoadedReportKey("");
            setReportReload((value) => value + 1);
            return;
          }
          setObservationsData(data);
        },
        () => {
          if (currentReportKey.current === reportKey) {
            setLoadedReportKey("");
            setReportError("No se pudieron recargar las observaciones");
          }
        }
      );
    }
  };

  const getUmbral = async (settingId, stateSet) => {
    const userId = rowData?.id;
    if (userId == undefined) return;

    callApiAsync(
      () => getSettingById(settingId),
      (data) => {
        const settingValue = data == null || data == undefined ? 0 : data;
        stateSet(settingValue);
      }
    );
  };

  useEffect(() => {
    getUmbral("SPECIFIC_COMPETENCY_THRESHOLD", setSpecifyUmbral);
    getUmbral("ORGANIZATIONAL_COMPETENCY_THRESHOLD", setOrganizationalUmbral);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setReportError("");
    setLoadedReportKey("");
    if (!rowData?.id || !selectedYear) {
      setReportLoading(false);
      return;
    }
    setReportLoading(true);
    const loadReport = async () => {
      try {
        const [manager, total, specific, organizational, observations] = await Promise.all([
          readReportData(() => getCallManagerReport360GraphData(rowData.id, selectedYear)),
          readReportData(() => getCallManagerTotal360GraphData(rowData.id, selectedYear)),
          readReportData(() => getCallSpecifyReport360GraphData(rowData.id, selectedYear)),
          readReportData(() => getCallOrganizationalReport360GraphData(rowData.id, selectedYear)),
          readReportData(() => getCallReport360Observations(rowData.id, selectedYear))
        ]);
        if (cancelled || currentReportKey.current !== reportKey) return;
        const periods = [manager.meta?.timeframeId, total.meta?.timeframeId, specific.meta?.timeframeId,
          organizational.meta?.timeframeId, observations.evaluationTimeframeId].map((id) => id == null ? null : Number(id));
        if (new Set(periods).size > 1) throw new Error("El periodo cambio durante la consulta");
        setManagerReport(manager);
        setManagerReportTotal(total);
        setSpecifyReport(specific);
        setOrganizationalReport(organizational);
        setObservationsData(observations);
        setLoadedReportKey(reportKey);
      } catch (error) {
        if (!cancelled && currentReportKey.current === reportKey) {
          setReportError("No se pudo cargar el informe del año seleccionado.");
        }
      } finally {
        if (!cancelled && currentReportKey.current === reportKey) setReportLoading(false);
      }
    };
    loadReport();
    return () => { cancelled = true; };
  }, [rowData?.id, selectedYear, reportKey, reportReload]);

  const tabData = [
    {
      name: "Observaciones",
      id: "0",
      content: (
        <>
          <ObservationsTab
            observationsData={observationsData}
            getObservationsData={getObservationsData}
            userIdSelected={rowData?.id}
            userData={userData}
            selectedYear={selectedYear}
          />
        </>
      ),
    },
    {
      name: "Específicas",
      id: "1",
      content: (
        <>
          <div>
            <p className="graph_title_report_360">Competencia</p>
            <CompetenceGraphSection
              type={0}
              data={specifyReport}
              umbral={specifyUmbral}
            />
          </div>
          <AuthorizedComponent
            allowedAuthority={[Page360report.specificComparative]}
            userAuthority={userData.authorityList}
          >
            <div className="table-container">
              <p className="graph_title_report_360">Comparativa</p>
              <ComparisonGraphSection
                type={0}
                data={managerReport}
                umbral={specifyUmbral}
              />
            </div>
          </AuthorizedComponent>

          <AuthorizedComponent
            allowedAuthority={[Page360report.specificTotal360]}
            userAuthority={userData.authorityList}
          >
            <div className="table-container">
              <p className="graph_title_report_360">Total 360</p>
              <Total360Graph
                type={0}
                data={managerReportTotal}
                umbral={specifyUmbral}
              />
            </div>
          </AuthorizedComponent>
        </>
      ),
    },
    {
      name: "Organizacionales",
      id: "2",
      content: (
        <>
          <div>
            <p className="graph_title_report_360">Competencia</p>
            <CompetenceGraphSection
              type={1}
              data={organizationalReport}
              umbral={organizationalUmbral}
            />
          </div>

          <AuthorizedComponent
            allowedAuthority={[Page360report.organizationalComparative]}
            userAuthority={userData.authorityList}
          >
            <div className="table-container">
              <p className="graph_title_report_360">Comparativa</p>
              <ComparisonGraphSection
                type={1}
                data={managerReport}
                umbral={organizationalUmbral}
              />
            </div>
          </AuthorizedComponent>

          <AuthorizedComponent
            allowedAuthority={[Page360report.organizationalTotal360]}
            userAuthority={userData.authorityList}
          >
            <div className="table-container">
              <p className="graph_title_report_360">Total 360</p>
              <Total360Graph
                type={1}
                data={managerReportTotal}
                umbral={organizationalUmbral}
              />
            </div>
          </AuthorizedComponent>
        </>
      ),
    },
  ];

  return (
    <div>
      <BreadCrumbComp />
      <div className="searchTitle">
        <h1 className="page-title">
          INFORME 360° - {rowData?.employee ? rowData.employee : ""}
          {selectedYear ? ` - ${selectedYear}` : ""}
        </h1>
        <div className="HContainer">
          <div className="HContainer">
            <Link to="/superior_rol_dashboard">
              <LaddaButton
                variant="contained"
                color={"black"}
                backgroundColor={"#F4D658"}
              >
                <div className="HContainer align-center">
                  <p className="button_text">Dashboard</p>
                </div>
              </LaddaButton>
            </Link>
          </div>
        </div>
      </div>

      <div className="report-results-header">
        <h1 className="title_report_360">
          Resultados Autoevaluación y Evaluadores
        </h1>
        <FormControl size="small" className="report-year-select" disabled={availableYears.length <= 1}>
          <InputLabel id="report-year-label">Año</InputLabel>
          <Select
            labelId="report-year-label"
            label="Año"
            value={selectedYear}
            onChange={(event) => setSelectedYear(Number(event.target.value))}
            displayEmpty
          >
            {availableYears.length === 0 && <MenuItem value="">Sin registros</MenuItem>}
            {availableYears.map((year) => <MenuItem key={year} value={year}>{year}</MenuItem>)}
          </Select>
        </FormControl>
      </div>
      {reportError && <Alert severity="error" sx={{ marginBottom: 2 }} action={
        <LaddaButton variant="contained" margin="0" onClick={() => setReportReload((value) => value + 1)}>Reintentar</LaddaButton>
      }>{reportError}</Alert>}
      {reportLoading && <CircularProgress size={24} aria-label="Cargando informe anual" />}
      {!rowData?.id && <Alert severity="info">Selecciona un empleado desde el Dashboard.</Alert>}
      {rowData?.id && !selectedYear && <Alert severity="info">No hay años con evaluaciones respondidas.</Alert>}
      {rowData?.id && selectedYear && <TabComponent panels={tabData.map((tab) => ({
        ...tab, content: loadedReportKey === reportKey ? tab.content : <></>
      }))} />}
    </div>
  );
};

export default Report_360;
